import React, { useEffect } from 'react';
import '../styles/About.css';

const ApiUsagePage = () => {
  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="api-usage-page">
      <h1 className="title">API Usage & Credits</h1>

      <div className="animate-on-scroll hero-section">
        <div className="card">
          <p className="card-text">
            This website is a personal, non-commercial project created for organizing and visualizing my own media collection. It relies on several external APIs to gather data, and this page acknowledges those sources with gratitude for enabling this project.
          </p>
        </div>
      </div>

      <section className="content-sections">
        <div className="animate-on-scroll section">
          <img src="/static/images/mal.png" alt="MyAnimeList homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/myanimelist.svg" alt="MAL" className="card-icon" />
              MyAnimeList (MAL)
            </h2>
            <p className="card-text">
              MyAnimeList powers the anime and manga sections with detailed metadata and vibrant cover images for thousands of series.
            </p>
            <div className="card-links">
              <a href="https://myanimelist.net/" target="_blank" rel="noopener noreferrer">MyAnimeList</a>
              • <a href="https://myanimelist.net/apiconfig/references/api/v2" target="_blank" rel="noopener noreferrer">MAL API Docs</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section reverse">
          <img src="/static/images/mangadex.png" alt="MangaDex homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/MangaDex.svg" alt="MangaDex" className="card-icon" />
              MangaDex (Fallback for Manga)
            </h2>
            <p className="card-text">
              MangaDex steps in when MAL lacks information or a title isn't listed, providing additional manga data.
            </p>
            <div className="card-links">
              <a href="https://mangadex.org" target="_blank" rel="noopener noreferrer">MangaDex Homepage</a>
              • <a href="https://api.mangadex.org/docs" target="_blank" rel="noopener noreferrer">MangaDex API Docs</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section">
          <img src="/static/images/steam.png" alt="Steam homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/steam.svg" alt="Steam" className="card-icon" />
              Steam + SteamSpy
            </h2>
            <p className="card-text">
              Steam's API fetches my personal library and achievements, while SteamSpy adds tags and player data for deeper insights.
            </p>
            <div className="card-links">
              <a href="https://store.steampowered.com/" target="_blank" rel="noopener noreferrer">Steam Store</a> 
              • <a href="https://developer.valvesoftware.com/wiki/Steam_Web_API" target="_blank" rel="noopener noreferrer">Steam API Docs</a> 
              • <a href="https://steamspy.com/" target="_blank" rel="noopener noreferrer">SteamSpy</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section reverse">
          <img src="/static/images/igdb.png" alt="IGDB homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/igdb.svg" alt="IGDB" className="card-icon" />
              IGDB
            </h2>
            <p className="card-text">
              IGDB covers non-Steam games like PSP or DS titles, providing artwork, genres, and release dates.
            </p>
            <div className="card-links">
              <a href="https://www.igdb.com/" target="_blank" rel="noopener noreferrer">IGDB Homepage</a>
              • <a href="https://api-docs.igdb.com/" target="_blank" rel="noopener noreferrer">IGDB API Docs</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section">
          <img src="/static/images/tmdb.png" alt="TMDB homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/film-solid.svg" alt="TMDB" className="card-icon" />
              TMDB
            </h2>
            <p className="card-text">
              TMDB drives the movie and TV sections with posters and detailed metadata.
            </p>
            <div className="card-links">
              <a href="https://www.themoviedb.org" target="_blank" rel="noopener noreferrer">TMDB Homepage</a>
              • <a href="https://www.themoviedb.org/documentation/api" target="_blank" rel="noopener noreferrer">TMDB API</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section reverse">
          <img src="/static/images/discogs.png" alt="Discogs homepage" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/discogs.svg" alt="Discogs" className="card-icon" />
              Discogs
            </h2>
            <p className="card-text">
              Discogs provides data and artwork for vinyls, CDs, and other music-related media.
            </p>
            <div className="card-links">
              <a href="https://www.discogs.com/" target="_blank" rel="noopener noreferrer">Discogs Homepage</a>
              • <a href="https://www.discogs.com/developers/" target="_blank" rel="noopener noreferrer">Discogs API</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll section">
          <img src="/static/images/login-background.jpg" alt="Image credits" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/image-solid.svg" alt="Image Credits" className="card-icon" />
              Image Credits
            </h2>
            <ul className="card-text">
              <li>Intro card by <a href="https://www.liamwong.com/akihabara/" target="_blank" rel="noopener noreferrer">[liamwong]</a></li>
              {/* <li>Invitations image by <a href="https://unsplash.com/@another_credit_name" target="_blank" rel="noopener noreferrer">[Photographer]</a></li> */}
            </ul>
          </div>
        </div>

        <div className="animate-on-scroll section reverse">
          <img src="/static/images/font-awesome.png" alt="Font Awesome" className="section-image" />
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/font-awesome-solid.svg" alt="Font Awesome" className="card-icon" />
              Icons
            </h2>
            <p className="card-text">
              All icons are sourced from Font Awesome.
            </p>
            <div className="card-links">
              <a href="https://fontawesome.com/" target="_blank" rel="noopener noreferrer">Font Awesome</a>
            </div>
          </div>
        </div>

        <div className="animate-on-scroll final-note">
          <div className="card">
            <h2 className="card-title">
              <img src="/static/icons/circle-info-solid.svg" alt="Info" className="card-icon" />
              Notes
            </h2>
            <p className="card-text">
              This is a private, self-hosted tracker. Data is occasionally refreshed and kept mostly up to date, but may still be outdated. Most items include an <strong>external link</strong> to their original source for the original and most accurate information.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ApiUsagePage;